import Box from "@/common/components/Box"
import TextField from "@/common/components/TextField"
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { LoginValidationSchema, loginSchema } from "../../schemas/login.schema";
import { SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { setInLocalStorage } from "@/common/services/localStorageGetSet";
import { login } from "../../services/login.service";
import { useAuth } from "@/common/stores/auth";
import Button from "@/common/components/Button";
import ForgotPasswordModal from "../../components/ForgotPasswordModal";
import { loginWithGoogle } from "../../services/loginWithGoogle.service";

const Login = () => {
  const [loginError, setLoginError] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const { updateAccessToken } = useAuth();

  const location = useLocation();
  const from = location?.state?.from?.pathname || '/';

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<LoginValidationSchema>({
    resolver: zodResolver(loginSchema)
  })

  const { isPending, mutate } = useMutation({
    mutationKey: ['login'],
    mutationFn: async (form: LoginValidationSchema) => login(form),
    onSuccess: (data) => {
      setLoginError(false);

      queryClient.clear();

      setInLocalStorage('access-token', data.accessToken);
      updateAccessToken(data.accessToken);

      navigate(from, { replace: true });
    },
    onError: (error) => {
      setLoginError(true);
      setError('password', { message: error.message });
    }
  })

  const onSubmit: SubmitHandler<LoginValidationSchema> = (data) => mutate(data);

  const handleRedirectGoogleLogin = () => {
    loginWithGoogle(from);
  }

  return (
    <div className="flex flex-col items-center gap-8 w-screen">
      <img src="/Logo.svg" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className=" flex flex-col gap-4 rounded-xl backdrop-blur-lg">
          <div className="flex flex-col gap-2 text-center">
            <h3 className="text-2xl font-bold">Bem vindo</h3>
            <p>Confirme seus dados para acessar a plataforma.</p>
          </div>

          <TextField
            label="Email"
            variant={errors.email?.message || loginError ? 'error' : 'default'}
            errorMessage={errors.email?.message || undefined}
            {...register('email')}
          />
          <TextField
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            RightIcon={showPassword ? <EyeOff /> : <Eye />}
            onIconClick={() => setShowPassword(!showPassword)}
            variant={errors.password?.message ? 'error' : 'default'}
            errorMessage={errors.password?.message || undefined}
            {...register('password')}
          />

          <div className="flex justify-end">
            <ForgotPasswordModal />
          </div>

          <Button
            type="submit"
            className="w-full bg-white-800 hover:bg-white-400 text-black rounded-b-lg rounded-t-md py-4"
            isLoading={isPending}
            spinnerClassName="fill-gray-800"
          >
            Entrar na conta
          </Button>

          <div className='flex items-center gap-2'>
            <p className='text-nowrap text-sm'>
              Ou entre com
            </p>

            <div className='h-px w-full bg-white-300' />
          </div>

          <Button
            className="w-full bg-white-200 hover:bg-white-300 text-white-800 rounded-b-lg rounded-t-md py-4 transition-colors"
            disabled={isPending}
            onClick={handleRedirectGoogleLogin}
          >
            <img src='/Google.svg' />

            Entre com o Google
          </Button>
        </Box>
      </form>

      <div className="flex flex-col items-center">
        <span className="text-[10px] italic font-light">Desenvolvido por</span>
        <img src='/Avantsoft.svg' />
      </div>
    </div>
  )
}

export default Login
